import { Injectable } from '@angular/core';

import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import type { UserReference } from '@recruitee/account-types';
import type { Candidate, CandidateReference } from '@recruitee/candidate-types';
import { HttpApiService } from '@recruitee/http';
import type {
  MailboxConvertData,
  MailboxSearchParams,
  MailboxThread,
} from '@recruitee/mailbox-types';
import type { OfferBriefReference } from '@recruitee/offers-types';
import type { PlacementReference } from '@recruitee/placements-types';
import type { StageReference } from '@recruitee/stages-types';
import type { Visibility } from '@recruitee/visibility-types';

export interface MailboxThreadsCounters {
  counters: { total: number; unread: number };
  threads: MailboxThread[];
}

export type MailboxThreadUpdate = Pick<
  MailboxThread,
  'id' | 'candidateId' | 'offerId' | 'archivedAt' | 'visibility'
>;

export type MailboxThreadsReferences =
  | CandidateReference
  | UserReference
  | OfferBriefReference
  | StageReference
  | PlacementReference;
export type References = { references: MailboxThreadsReferences[] };

@Injectable({ providedIn: 'root' })
export class MailboxThreadsHttpClient {
  constructor(private httpApi: HttpApiService) {}

  public fetchCollection(
    params: Partial<MailboxSearchParams>,
  ): Observable<MailboxThreadsCounters & References> {
    return this.httpApi.get('/mailbox/threads', params);
  }

  public fetchOne({
    id,
  }: {
    id: number;
  }): Observable<{ thread: MailboxThread; related: MailboxThread[] } & References> {
    return this.httpApi.get(`/mailbox/threads/${id}`).pipe(
      map(data => ({
        ...data,
        thread: {
          ...data.thread,
          relatedIds: data.related.map(({ id }: { id: number }) => id),
        },
      })),
    );
  }

  public fetchCandidateCollection({
    candidateId,
  }: {
    candidateId: number;
  }): Observable<{ threads: MailboxThread[] } & References> {
    return this.httpApi.get(`/mailbox/candidate/${candidateId}`).pipe(
      map(data => {
        const filteredThreads: MailboxThread[] = data.threads.filter(
          (t: MailboxThread) => (t.messages || []).length > 0,
        );

        return {
          threads: filteredThreads.sort((a, b) =>
            moment(a.messages[0].timestamp) < moment(b.messages[0].timestamp) ? 1 : -1,
          ),
          references: data.references,
        };
      }),
    );
  }

  public update(
    { id }: { id: number },
    params: { candidateId?: number; visibility?: Visibility },
  ): Observable<MailboxThreadUpdate> {
    return this.httpApi.patch(`/mailbox/threads/${id}`, null, params);
  }

  public delete({ id }: { id: number }): Observable<void> {
    return this.httpApi.delete(`/mailbox/threads/${id}`);
  }

  public markRead({ id }: { id: number }, read: boolean): Observable<{ read: boolean }> {
    return this.httpApi
      .patch('mailbox/threads/mark', null, { id, read })
      .pipe(map(_ => ({ read })));
  }

  public markAllAsRead(
    searchParams: Partial<MailboxSearchParams>,
    until: string | undefined,
  ): Observable<void> {
    const params = { ...searchParams, until, read: true };
    return this.httpApi.patch(`/mailbox/threads/mark`, null, params);
  }

  public markArchived(
    { id }: { id: number },
    archived: boolean,
  ): Observable<{ archived: boolean; id: number }> {
    return this.httpApi
      .patch('mailbox/threads/mark', null, { id, archived })
      .pipe(map(data => ({ id, archived })));
  }

  public convert({
    data,
    offers = null,
    talentPools = null,
    mailboxThreadId = null,
    attachmentAssignmentId = null,
  }: MailboxConvertData): Observable<{ candidate: Candidate } & References> {
    const body = {
      candidate: data,
      offers,
      talentPools,
      threadId: mailboxThreadId,
      attachmentAssignmentId,
    };
    return this.httpApi.post('/mailbox/convert', null, body);
  }

  public resyncEmailsConversation(candidateId: number): Observable<any> {
    return this.httpApi.patch(`/mailbox/candidate/${candidateId}/fetch`, null, null);
  }
}
